import { ActionSelectMenu, Button, ControlledCheckbox } from '@act/storybook';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  Control,
  Controller,
  FieldValues,
  Path,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form';
import { membersRequests } from 'redux/requests/members.request';
import { useApiCall } from 'redux/utils/api-hook';
import {
  ButtonPanel,
  FilterContainer,
  OptionsContainer,
} from 'utilities/filters/filter-styled-components';
import {
  BaseFilterConfig,
  PaginatedFilterConfig,
} from 'utilities/filters/filters.type';
import { SearchBar, SelectOption } from '../../components/search-bar';
import { testNumberString } from '../calls-texts-utils';

import './text-filters.scss';

interface TextFiltersProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>;
  activeFilterCount: number;
  currentFilterConfig: BaseFilterConfig;
  setValue: UseFormSetValue<PaginatedFilterConfig>;
  getValues: UseFormGetValues<PaginatedFilterConfig>;
  handleReset: () => void;
  handleOpenFilters: () => void;
  handleFiltering: () => void;
}

const TextFilters = <T,>({
  control,
  activeFilterCount,
  currentFilterConfig,
  setValue,
  getValues,
  handleReset,
  handleOpenFilters,
  handleFiltering,
}: TextFiltersProps<T>) => {
  const [typeaheadOptions, setTypeaheadOptions] = useState<SelectOption[]>([]);
  const [typeaheadUsers, isLoadingTypeahead, getTypeaheadUsers] = useApiCall(
    membersRequests.getPaginatedMembersForTable
  );

  useEffect(() => {
    if (typeaheadUsers?.users) {
      const options = typeaheadUsers.users.map(u => {
        return {
          value: u.id,
          label: `${u.firstName} ${u.lastName}`,
        };
      });
      setTypeaheadOptions(options);
    } else {
      setTypeaheadOptions([]);
    }
  }, [typeaheadUsers]);

  function setTypeaheadSearchTerm(searchTerm: string) {
    const overrideFilters = {
      searchTerm,
      'page-start': 0,
      'page-size': 10,
      includeCount: true,
      orderBy: null,
      orderDirection: null,
      includeDetails: true,
    };
    const filterConfig = {
      ...currentFilterConfig,
      ...overrideFilters,
    };
    getTypeaheadUsers({ params: filterConfig });
  }

  return (
    <FilterContainer>
      <Controller
        control={control}
        name={'searchTerm' as Path<T>}
        render={({ field: { onChange, value } }) => {
          return (
            <SearchBar
              value={value as string}
              label="Search by member name or phone number"
              options={typeaheadOptions}
              isLoading={isLoadingTypeahead}
              setTypeaheadSearchTerm={setTypeaheadSearchTerm}
              setSearchTerm={(searchTerm: string | SelectOption) => {
                setValue('searchTermType', null);
                setValue('searchTerm', null);
                if (typeof searchTerm === 'string') {
                  // check to see if searching a phone number or name
                  if (testNumberString(searchTerm)) {
                    setValue('searchTermType', 'number');
                  } else {
                    setValue('searchTermType', 'string');
                  }
                  setValue('searchTerm', searchTerm);
                  onChange(searchTerm);
                } else if (searchTerm?.label) {
                  onChange(searchTerm.label);
                  setValue('subjectIds', searchTerm.value);
                  setValue('searchTermType', 'subjectId');
                } else {
                  onChange(null);
                  setValue('subjectIds', null);
                  setValue('searchTermType', null);
                }
                handleFiltering();
              }}
            />
          );
        }}
      />
      <OptionsContainer>
        <div className="checkboxes">
          <ControlledCheckbox
            ariaLabel="Only unassigned"
            control={control}
            defaultChecked={false}
            label="Only unassigned"
            name={'onlyUnassignedMessages' as Path<T>}
          />

          <ControlledCheckbox
            ariaLabel="Only my texts"
            control={control}
            defaultChecked={false}
            onChange={handleFiltering}
            label="Only my texts"
            name={'showOnlyMyTexts' as Path<T>}
          />
        </div>

        <ButtonPanel>
          <ActionSelectMenu
            menuIcon="calendar_month"
            actions={[
              {
                display: 'Last 1 Day',
                defaultSelected: getValues('lastDaysFromNow') === 1,
                callback: () => {
                  setValue('lastDaysFromNow', 1);
                  handleFiltering();
                },
              },
              {
                display: 'Last 7 Days',
                defaultSelected: getValues('lastDaysFromNow') === 7,
                callback: () => {
                  setValue('lastDaysFromNow', 7);
                  handleFiltering();
                },
              },
              {
                display: 'Last 14 Days',
                defaultSelected: getValues('lastDaysFromNow') === 7,
                callback: () => {
                  setValue('lastDaysFromNow', 14);
                  handleFiltering();
                },
              },
              {
                display: 'Last 30 Days',
                defaultSelected: getValues('lastDaysFromNow') === 7,
                callback: () => {
                  setValue('lastDaysFromNow', 7);
                  handleFiltering();
                },
              },
              {
                display: 'Last 60 Days',
                defaultSelected: getValues('lastDaysFromNow') === 60,
                callback: () => {
                  setValue('lastDaysFromNow', 60);
                  handleFiltering();
                },
              },
              {
                display: 'Last 90 Days',
                defaultSelected: getValues('lastDaysFromNow') === 90,
                callback: () => {
                  setValue('lastDaysFromNow', 90);
                  handleFiltering();
                },
              },
              {
                display: 'All',
                defaultSelected: !getValues('lastDaysFromNow'),
                callback: () => {
                  setValue('lastDaysFromNow', null);
                  handleFiltering();
                },
              },
            ]}
          />
          <Button variant="text" onClick={handleReset}>
            <Typography variant="labelLarge">Clear filters</Typography>
          </Button>
          <Button
            startIcon={<FilterListIcon />}
            variant="outlined"
            onClick={handleOpenFilters}
          >
            <Typography variant="labelLarge">
              Filters {activeFilterCount > 0 ? `(${activeFilterCount})` : ''}
            </Typography>
          </Button>
        </ButtonPanel>
      </OptionsContainer>
    </FilterContainer>
  );
};

export default TextFilters;
